import { useCallback, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import cn from 'classnames';

import DndContext from 'components/dnd/DndContext.component';
import DndDroppable from 'components/dnd/DndDroppable.component';
import Button from 'components/form/Button.component';
import { useTranslation } from 'providers/GILT.provider';
import { useTheme } from 'providers/Theme.provider';
import { deleteAt, insertAt } from 'utils/arrays/array.util';
import isEmptyArray from 'utils/predicates/array/isEmptyArray.util';

import { MULTI_OPTION_TYPES, QUESTION_LIMITS } from '../consultationForms.const';

import { AddQuestionModal } from './AddQuestionModal.component';
import { QuestionCard } from './QuestionCard.component';

import CN from './QuestionsList.module.scss';

const EMPTY_QUESTIONS = [];

export const QuestionsList = () => {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const { setValue } = useFormContext();
  const questions = useWatch({ name: 'questions' }) ?? EMPTY_QUESTIONS;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);

  const atMaxQuestions = questions.length >= QUESTION_LIMITS.MAX_QUESTIONS_PER_FORM;

  const openAddModal = () => {
    if (atMaxQuestions) return;
    setEditingQuestion(null);
    setIsModalOpen(true);
  };

  const openEditModal = (index) => {
    setEditingQuestion(questions[index]);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingQuestion(null);
  };

  const handleSaveQuestion = useCallback(
    (question) => {
      if (editingQuestion !== null) {
        const updated = questions.map((q) => (q.id === editingQuestion.id ? { ...q, ...question } : q));
        setValue('questions', updated, { shouldDirty: true });
      } else {
        setValue('questions', [...questions, question], { shouldDirty: true });
      }
      closeModal();
    },
    [editingQuestion, questions, setValue],
  );

  const handleDragEnd = useCallback(
    (result) => {
      const { source, destination } = result ?? {};
      if (!destination || source.index === destination.index) return;
      const moved = questions[source.index];
      const without = deleteAt({ array: questions, index: source.index });
      const reordered = insertAt({ array: without, what: moved, index: destination.index });
      setValue('questions', reordered, { shouldDirty: true });
    },
    [questions, setValue],
  );

  const handleChangeType = useCallback(
    (index, newType) => {
      const selectedQuestion = questions[index];
      if (!selectedQuestion || newType === selectedQuestion.type) return;

      const oldType = selectedQuestion.type;
      const touchesMulti = MULTI_OPTION_TYPES.includes(oldType) || MULTI_OPTION_TYPES.includes(newType);

      if (!touchesMulti) {
        const updated = questions.map((item, i) => (i === index ? { ...item, type: newType } : item));
        setValue('questions', updated, { shouldDirty: true });
        return;
      }

      const synthetic = { ...selectedQuestion, type: newType };

      if (MULTI_OPTION_TYPES.includes(oldType) && MULTI_OPTION_TYPES.includes(newType)) {
        const raw = Array.isArray(selectedQuestion.options) && selectedQuestion.options.length ? selectedQuestion.options.map((o) => (typeof o === 'string' ? o : '')) : [];
        synthetic.options = [...raw];
      } else if (MULTI_OPTION_TYPES.includes(newType) && !MULTI_OPTION_TYPES.includes(oldType)) {
        synthetic.options = [];
      }

      setEditingQuestion(synthetic);
      setIsModalOpen(true);
    },
    [questions, setValue],
  );

  const handleToggleRequired = useCallback(
    (index) => {
      const updated = questions.map((q, i) => (i === index ? { ...q, required: !q.required } : q));
      setValue('questions', updated, { shouldDirty: true });
    },
    [questions, setValue],
  );

  const handleDuplicate = useCallback(
    (index) => {
      if (atMaxQuestions) return;
      const duplicate = { ...questions[index], id: `q_${Date.now()}_${Math.random().toString(36).slice(2, 7)}` };
      setValue('questions', insertAt({ array: questions, what: duplicate, index: index + 1 }), { shouldDirty: true });
    },
    [questions, setValue, atMaxQuestions],
  );

  const handleDelete = useCallback(
    (index) => {
      setValue(
        'questions',
        questions.filter((_, i) => i !== index),
        { shouldDirty: true },
      );
    },
    [questions, setValue],
  );

  const isEmpty = isEmptyArray(questions);

  return (
    <div data-bem="QuestionsList" className={cn(CN.component, { [CN.darkMode]: isDarkMode })}>
      <DndContext tag="QuestionsList" onDragEnd={handleDragEnd}>
        <DndDroppable tag="QuestionsList" droppableId={JSON.stringify({ type: 'questions' })}>
          {(provided) => (
            <div ref={provided.innerRef} {...provided.droppableProps}>
              {questions.map((question, index) => (
                <QuestionCard
                  key={question.id != null ? String(question.id) : `question_${index}`}
                  question={question}
                  index={index}
                  onChangeType={handleChangeType}
                  onToggleRequired={handleToggleRequired}
                  onDuplicate={handleDuplicate}
                  onDelete={handleDelete}
                  onEditQuestionText={openEditModal}
                />
              ))}
              {provided.placeholder}
            </div>
          )}
        </DndDroppable>
      </DndContext>

      {atMaxQuestions ? (
        <p className={CN.limitMessage} data-testid="QuestionsList__maxQuestionsMessage">
          {t('consultationForms.builder.maxQuestionsReached')}
        </p>
      ) : (
        <Button variant="stripped" data-testid="QuestionsList__addButton" className={CN.addButton} onClick={openAddModal}>
          {isEmpty ? t('consultationForms.builder.addFirstQuestion') : t('consultationForms.builder.addQuestion')}
        </Button>
      )}

      {isModalOpen && <AddQuestionModal isOpen={isModalOpen} onClose={closeModal} onSave={handleSaveQuestion} initialQuestion={editingQuestion} />}
    </div>
  );
};
